import { InteractionStatus, type Configuration } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  LogOut,
  RefreshCw,
  Search,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID;
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
const apiClientId = import.meta.env.VITE_API_CLIENT_ID;
const apiBase = import.meta.env.VITE_API_BASE_URL;
export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: "sessionStorage" },
};
const scopes = [`api://${apiClientId}/operations_access`];
type Evidence = { type: string; description: string; reference?: string };
type EvidenceEvent = {
  id: string;
  actorSubject: string;
  notes: string | null;
  evidence: Evidence[];
  createdAt: string;
};
type Case = {
  id: string;
  status: string;
  createdAt: string;
  assignedAnalystSubject: string | null;
  vendorReference: string | null;
  organization: string;
  domain: string;
  provider: string;
  signalFamily: string;
  previousValue: string;
  currentValue: string;
  severity: string;
  detectedAt: string;
  customerAuthorized: boolean;
  playbook: null | {
    version: string;
    submissionUrl: string | null;
    instructions: string[];
    requiredEvidence: string[];
  };
  evidenceHistory: EvidenceEvent[];
};
const evidenceLabels: Record<string, string> = {
  screenshot: "Classification screenshot",
  dns_record: "DNS ownership record",
  email_header: "Email header",
  http_response: "HTTP response validation",
  customer_statement: "Customer rationale",
  provider_correspondence: "Provider correspondence",
  other: "Other evidence",
};

export function App() {
  const authenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const [cases, setCases] = useState<Case[]>([]);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const call = useCallback(
    async (path: string, init?: RequestInit) => {
      const request = async (forceRefresh = false) => {
        const token = await instance.acquireTokenSilent({
          account: accounts[0]!,
          scopes,
          forceRefresh,
        });
        return fetch(`${apiBase}${path}`, {
          ...init,
          headers: {
            ...init?.headers,
            Authorization: `Bearer ${token.accessToken}`,
            "Content-Type": "application/json",
          },
        });
      };
      let response = await request();
      if (response.status === 401) response = await request(true);
      if (!response.ok)
        throw new Error(`Operations API returned ${response.status}`);
      return response.json();
    },
    [accounts, instance],
  );
  const load = useCallback(async () => {
    try {
      setError("");
      const result = await call("/v1/operations/cases");
      setCases(
        (Array.isArray(result.cases) ? result.cases : []).map((item: Case) => ({
          ...item,
          evidenceHistory: Array.isArray(item.evidenceHistory)
            ? item.evidenceHistory.map((entry) => ({
                ...entry,
                evidence: Array.isArray(entry.evidence) ? entry.evidence : [],
              }))
            : [],
          playbook: item.playbook
            ? {
                ...item.playbook,
                instructions: Array.isArray(item.playbook.instructions)
                  ? item.playbook.instructions
                  : [],
                requiredEvidence: Array.isArray(item.playbook.requiredEvidence)
                  ? item.playbook.requiredEvidence
                  : [],
              }
            : null,
        })),
      );
    } catch {
      setError("The analyst queue is unavailable.");
    }
  }, [call]);
  useEffect(() => {
    if (authenticated) void load();
  }, [authenticated, load]);
  const shown = useMemo(
    () =>
      cases.filter((item) =>
        `${item.organization} ${item.domain} ${item.provider} ${item.status}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [cases, query],
  );
  async function act(
    item: Case,
    action: string,
    fields: Record<string, unknown> = {},
  ) {
    await call(`/v1/operations/cases/${item.id}`, {
      method: "PATCH",
      body: JSON.stringify({ action, ...fields }),
    });
    await load();
  }
  if (inProgress !== InteractionStatus.None)
    return <main className="center">Preparing secure sign-in…</main>;
  if (!authenticated)
    return (
      <main className="center">
        <section className="signin">
          <strong className="wordmark">Trustemetry</strong>
          <p className="eyebrow">Workforce operations</p>
          <h1>Analyst remediation console</h1>
          <p>Restricted to authorized Trustemetry personnel.</p>
          <button onClick={() => void instance.loginRedirect({ scopes })}>
            Sign in with Trustemetry
          </button>
        </section>
      </main>
    );
  return (
    <div>
      <header>
        <div>
          <strong>Trustemetry</strong>
          <span>Operations</span>
        </div>
        <button
          className="ghost"
          onClick={() => void instance.logoutRedirect()}
        >
          <LogOut size={16} />
          Sign out
        </button>
      </header>
      <main className="page">
        <div className="title">
          <div>
            <p className="eyebrow">Human-led remediation</p>
            <h1>Analyst queue</h1>
            <p>
              Investigate material classification changes, collect evidence, and
              track vendor submissions.
            </p>
          </div>
          <button className="ghost" onClick={() => void load()}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
        <div className="toolbar">
          <Search size={17} />
          <input
            aria-label="Search cases"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search organization, domain, provider, or status"
          />
        </div>
        {error && (
          <p className="error">
            <AlertTriangle size={17} />
            {error}
          </p>
        )}
        <section className="queue">
          {shown.map((item) => {
            const recorded = new Set(
              item.evidenceHistory.flatMap((entry) =>
                entry.evidence.map((evidence) => evidence.type),
              ),
            );
            const missing =
              item.playbook?.requiredEvidence.filter(
                (type) => !recorded.has(type),
              ) ?? [];
            const assigned = Boolean(item.assignedAnalystSubject);
            return (
              <article className="case" key={item.id}>
                <div className="case-head">
                  <div>
                    <span className={`severity ${item.severity}`}>
                      {item.severity}
                    </span>
                    <h2>{item.domain}</h2>
                    <p>{item.organization}</p>
                  </div>
                  <span className="status">
                    {item.status.replaceAll("_", " ")}
                  </span>
                </div>
                <div className="change">
                  <div>
                    <small>Previous</small>
                    <strong>{item.previousValue}</strong>
                  </div>
                  <span>→</span>
                  <div>
                    <small>Current</small>
                    <strong>{item.currentValue}</strong>
                  </div>
                </div>
                <dl>
                  <div>
                    <dt>Provider</dt>
                    <dd>{item.provider}</dd>
                  </div>
                  <div>
                    <dt>Detected</dt>
                    <dd>{new Date(item.detectedAt).toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>Customer authorization</dt>
                    <dd>
                      {item.customerAuthorized
                        ? "Received"
                        : "Waiting for customer approval"}
                    </dd>
                  </div>
                  <div>
                    <dt>Assigned analyst</dt>
                    <dd>{assigned ? "Assigned" : "Unassigned"}</dd>
                  </div>
                </dl>
                {item.playbook ? (
                  <details open={item.customerAuthorized}>
                    <summary>
                      Provider playbook · {item.playbook.version}
                    </summary>
                    <ol>
                      {item.playbook.instructions.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                    <p>
                      <strong>Evidence checklist</strong>
                    </p>
                    <ul>
                      {item.playbook.requiredEvidence.map((type) => (
                        <li key={type}>
                          {recorded.has(type) ? "✓" : "○"}{" "}
                          {evidenceLabels[type] ?? type}
                        </li>
                      ))}
                    </ul>
                    {item.playbook.submissionUrl &&
                      (item.customerAuthorized ? (
                        <a
                          href={item.playbook.submissionUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open recategorization portal
                        </a>
                      ) : (
                        <p>Submission link unlocks after customer approval.</p>
                      ))}
                  </details>
                ) : (
                  <p className="error">
                    <AlertTriangle size={17} />
                    No active provider playbook. Do not submit this case.
                  </p>
                )}
                <EvidenceForm
                  item={item}
                  disabled={!assigned}
                  save={(fields) => act(item, "add_evidence", fields)}
                />
                <div className="actions">
                  {!assigned && (
                    <button onClick={() => void act(item, "assign_self")}>
                      <ClipboardList size={15} />
                      Assign to me
                    </button>
                  )}
                  <button
                    disabled={
                      !item.customerAuthorized ||
                      !assigned ||
                      !["authorized", "in_progress"].includes(item.status)
                    }
                    onClick={() => void act(item, "start_investigation")}
                  >
                    Start investigation
                  </button>
                  <button
                    disabled={item.status !== "submitted" || !assigned}
                    onClick={() => void act(item, "resolve")}
                  >
                    <CheckCircle2 size={15} />
                    Resolve
                  </button>
                </div>
                <SubmissionForm
                  item={item}
                  missing={missing}
                  submit={(fields) => act(item, "submit_to_vendor", fields)}
                />
              </article>
            );
          })}
          {shown.length === 0 && !error && (
            <div className="empty">
              <CheckCircle2 size={28} />
              <strong>No matching remediation cases</strong>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function EvidenceForm({
  item,
  disabled,
  save,
}: {
  item: Case;
  disabled: boolean;
  save: (fields: Record<string, unknown>) => Promise<void>;
}) {
  const [type, setType] = useState("screenshot");
  const [description, setDescription] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage("Saving…");
    try {
      await save({
        notes,
        evidence: [
          {
            type,
            description,
            ...(reference.trim() ? { reference: reference.trim() } : {}),
          },
        ],
      });
      setDescription("");
      setReference("");
      setNotes("");
      setMessage("Evidence saved.");
    } catch {
      setMessage("Evidence could not be saved.");
    }
  }
  return (
    <details className="evidence">
      <summary>
        Evidence ·{" "}
        {item.evidenceHistory?.flatMap((entry) => entry.evidence).length ?? 0}{" "}
        items
      </summary>
      {disabled && <p>Assign this case to yourself before adding evidence.</p>}
      <form onSubmit={(e) => void submit(e)}>
        <fieldset disabled={disabled}>
          <label>
            Evidence type
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {Object.entries(evidenceLabels).map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Description
            <textarea
              required
              maxLength={1000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label>
            Reference or secure artifact location
            <input
              maxLength={1000}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Case ID, URL, or approved storage reference"
            />
          </label>
          <label>
            Analyst notes
            <textarea
              maxLength={4000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <button type="submit">Add evidence</button>
        </fieldset>
        <span role="status">{message}</span>
      </form>
      {item.evidenceHistory?.map((entry) => (
        <div className="evidence-event" key={entry.id}>
          <small>
            {new Date(entry.createdAt).toLocaleString()} · {entry.actorSubject}
          </small>
          {entry.notes && <p>{entry.notes}</p>}
          {entry.evidence.map((evidence, index) => (
            <p key={`${entry.id}-${index}`}>
              <strong>{evidenceLabels[evidence.type] ?? evidence.type}:</strong>{" "}
              {evidence.description}
              {evidence.reference && <> · {evidence.reference}</>}
            </p>
          ))}
        </div>
      ))}
    </details>
  );
}

function SubmissionForm({
  item,
  missing,
  submit,
}: {
  item: Case;
  missing: string[];
  submit: (fields: Record<string, unknown>) => Promise<void>;
}) {
  const [reference, setReference] = useState("");
  const [message, setMessage] = useState("");
  const enabled =
    item.customerAuthorized &&
    Boolean(item.assignedAnalystSubject) &&
    item.status === "in_progress" &&
    Boolean(item.playbook) &&
    missing.length === 0;
  async function send(event: FormEvent) {
    event.preventDefault();
    setMessage("Recording…");
    try {
      await submit({
        vendorReference: reference,
        notes: "Provider submission recorded by assigned analyst.",
      });
      setMessage("Submission recorded.");
    } catch {
      setMessage(
        "Submission could not be recorded. Confirm authorization, assignment, evidence, and reference.",
      );
    }
  }
  if (
    item.status === "submitted" ||
    item.status === "resolved" ||
    item.status === "closed"
  )
    return (
      <p>
        <strong>Provider reference:</strong>{" "}
        {item.vendorReference ?? "Not recorded"}
      </p>
    );
  return (
    <form className="submission" onSubmit={(e) => void send(e)}>
      <label>
        Provider ticket or confirmation reference
        <input
          required
          maxLength={300}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Required after submitting through the provider portal"
        />
      </label>
      <button disabled={!enabled || !reference.trim()} type="submit">
        Mark provider request submitted
      </button>
      {missing.length > 0 && (
        <small>
          Missing required evidence:{" "}
          {missing.map((type) => evidenceLabels[type] ?? type).join(", ")}
        </small>
      )}
      <span role="status">{message}</span>
    </form>
  );
}
